import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Eye, EyeOff, ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { signUp } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import MiniMatrixPreview from '@/components/auth/MiniMatrixPreview';
import MiniAIAssistantPreview from '@/components/auth/MiniAIAssistantPreview';
import { PageTransition } from '@/components/PageTransition';

const SignupPage = () => {
  const navigate = useNavigate();
  usePageTitle('Create Account');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    password: '',
    confirmPassword: ''
  });

  const carouselSlides = [
    {
      title: "AI Compliance Assistant",
      description: "Your personal AI assistant handles bookings, recommendations, and compliance queries.",
      component: MiniAIAssistantPreview,
      gradient: "from-violet-500 to-purple-600"
    },
    {
      title: "Visual Compliance Matrix",
      description: "Track your entire team's training status in one powerful, real-time dashboard.",
      component: MiniMatrixPreview,
      gradient: "from-emerald-500 to-teal-600"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (!agreeToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Use custom signup edge function to bypass Supabase's default email
      const { data: signupResponse, error: functionError } = await supabase.functions.invoke('custom-signup', {
        body: {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          company: formData.company
        }
      });

      if (functionError) {
        setError(functionError.message || 'Failed to create account');
      } else if (!signupResponse?.success) {
        setError(signupResponse?.error || 'Failed to create account');
      } else {
        // Store email for resend functionality
        localStorage.setItem('signupEmail', formData.email);
        localStorage.setItem('signupUserId', signupResponse.data?.user?.id);

        console.log('Account created successfully:', signupResponse);

        if (signupResponse.emailSent) {
          console.log('N8N email triggered successfully');
        } else {
          console.log('Account created but email may not have been sent');
        }

        // Navigate to verification page
        navigate('/verify');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageTransition>
    <div className="min-h-screen flex">
      {/* Left Panel - Carousel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        {/* Dynamic Gradient Background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${carouselSlides[currentSlide].gradient} opacity-20 transition-all duration-1000`} />

        {/* Carousel Content */}
        <div className="relative z-10 flex flex-col w-full h-full">
          {/* Top Section with Text */}
          <div className="px-12 pt-12 pb-6 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              {carouselSlides[currentSlide].title}
            </h1>
            <p className="text-lg text-gray-300 max-w-md mx-auto">
              {carouselSlides[currentSlide].description}
            </p>
          </div>

          {/* Component Preview Area */}
          <div className="flex-1 relative">
            {carouselSlides.map((slide, index) => {
              const PreviewComponent = slide.component;
              return (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-500 ${
                    index === currentSlide
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-95 pointer-events-none'
                  }`}
                >
                  <PreviewComponent isActive={index === currentSlide} />
                </div>
              );
            })}
          </div>

          {/* Bottom Navigation */}
          <div className="px-12 py-8 flex items-center justify-between">
            {/* Slide Indicators */}
            <div className="flex space-x-2">
              {carouselSlides.map((_, index) => (
                <button
                  key={index}
                  className={`h-2 transition-all duration-300 rounded-full ${
                    index === currentSlide
                      ? 'w-8 bg-white'
                      : 'w-2 bg-white/30 hover:bg-white/50'
                  }`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={prevSlide}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={nextSlide}
                className="text-white hover:bg-white/10"
              >
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
      </div>

      {/* Right Panel - Signup Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-none">
            <CardHeader className="text-center pb-8">
              <div className="mb-6">
                <img
                  src="https://storage.googleapis.com/trainingmatrix/images/tmlogoiconv2.png"
                  alt="Ai Skills Matrix"
                  className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-xl drop-shadow-md"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">
                Create your account
              </CardTitle>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                      First name
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="h-11"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-medium text-slate-700">
                      Last name
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="h-11"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="h-11"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-sm font-medium text-slate-700">
                    Company name
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    placeholder="Your company name"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="h-11"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="h-11 pr-10"
                      required
                      disabled={isLoading}
                      minLength={6}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-11 px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-500" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                    Confirm password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className="h-11 pr-10"
                      required
                      disabled={isLoading}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-11 px-3 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-500" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={agreeToTerms}
                    onCheckedChange={setAgreeToTerms}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="terms"
                    className="text-sm text-slate-600 leading-5 cursor-pointer"
                  >
                    I agree to the{' '}
                    <Button
                      variant="link"
                      className="px-0 h-auto text-sm text-slate-900 hover:text-slate-700"
                    >
                      Terms of Service
                    </Button>{' '}
                    and{' '}
                    <Button
                      variant="link"
                      className="px-0 h-auto text-sm text-slate-900 hover:text-slate-700"
                    >
                      Privacy Policy
                    </Button>
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                  disabled={!agreeToTerms || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create account'
                  )}
                </Button>

              </form>
            </CardContent>

            <CardFooter className="justify-center pt-6">
              <p className="text-sm text-slate-600">
                Already have an account?{' '}
                <Button
                  variant="link"
                  className="px-0 text-slate-900 hover:text-slate-700"
                  onClick={() => navigate('/login')}
                >
                  Sign in
                </Button>
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
    </PageTransition>
  );
};

export default SignupPage;